import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ArrowRight, ChevronRight, Compass } from "lucide-react";

const TOC = [
  { id: "comparison", label: "Quick Comparison Table" },
  { id: "reviews", label: "Top Rated Solar Panels" },
  { id: "by-use-case", label: "Best Panels by Use Case" },
  { id: "buying-guide", label: "How to Choose" },
  { id: "faq", label: "FAQ" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "What Are the Best Solar Panels? Buyer's Guide by Use Case (2026)",
  "description": "Find the best solar panels by use case — RV, van life, camping, EcoFlow, flexible, bifacial, and high-efficiency options.",
  "url": "https://highestefficiencysolarcell.com/best-solar-panels",
  "dateModified": "2026-07-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

const USE_CASES = [
  { label: "Best for RVs", path: "/best-solar-panels-for-rv", desc: "Rigid rooftop panels built for road vibration and flat mounting." },
  { label: "Best for Van Life", path: "/solar-panels-for-van-life", desc: "Flexible and rigid panels for Sprinter, Transit, and Promaster builds." },
  { label: "Best for Camping", path: "/best-portable-solar-panels", desc: "Foldable, lightweight panels for car camping and off-grid trips." },
  { label: "Best for EcoFlow", path: "/solar-panel-for-ecoflow", desc: "Panels with correct voltage and connectors for EcoFlow stations." },
  { label: "Best Flexible", path: "/flexible-solar-panels", desc: "Bendable panels for curved roofs, boats, and low-profile installs." },
  { label: "Best Bifacial", path: "/bifacial-solar-panels", desc: "Dual-sided panels that capture reflected light for extra yield." },
  { label: "Most Efficient", path: "/highest-efficiency-solar-panels", desc: "Highest-efficiency commercially available panels (HJT, TOPCon, IBC)." },
  { label: "Best High-Efficiency", path: "/best-high-efficiency-solar-panels", desc: "Top-rated high-efficiency panels with pros, cons, and reviews." },
  { label: "Best for Solar Generators", path: "/best-solar-panels-for-solar-generators", desc: "Panels compatible with Jackery, Bluetti, and EcoFlow stations." },
];

export default function BestSolarPanels() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const featured = products.filter((p) => p.featured).slice(0, 8);
  const displayProducts = featured.length > 0 ? featured : products.slice(0, 8);

  return (
    <>
      <SEOHead
        title="Best Solar Panels 2026 | Buyer's Guide by Use Case (RV, Van, Camping, EcoFlow)"
        description="What are the best solar panels? Compare top-rated solar panels by use case — RV, van life, camping, EcoFlow, flexible, bifacial, and high-efficiency picks. Updated 2026."
        canonical="https://highestefficiencysolarcell.com/best-solar-panels"
        schema={SCHEMA}
      />
      <div className="pb-28 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">Best Solar Panels</span>
          </nav>

          <div className="max-w-4xl mb-2">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
              What Are the Best Solar Panels? Buyer's Guide by Use Case
            </h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
              There is no single "best solar panel" — the right panel depends on whether you're powering an RV,
              a van build, a camping trip, or an EcoFlow power station. This guide breaks down the best solar panels
              by use case so you can find the right match for your setup.
            </p>

            <AffiliateDisclosure />
            <TableOfContents items={TOC} />
          </div>

          <section id="comparison" className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-4">Quick Comparison Table</h2>
          </section>

          {displayProducts.length > 0 && (
            <>
              <ComparisonTable products={displayProducts} />
              <div className="mt-16" id="reviews">
                <h2 className="font-heading font-bold text-3xl mb-8">Top Rated Solar Panels</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            </>
          )}

          <section id="by-use-case" className="mt-16">
            <h2 className="font-heading font-bold text-3xl mb-6">Best Solar Panels by Use Case</h2>
            <p className="text-muted-foreground mb-6 max-w-3xl">
              The fastest way to find the right panel is to start with your use case. Each link below goes to a
              dedicated guide with product recommendations, comparison tables, and buying tips for that specific
              application.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {USE_CASES.map((uc) => (
                <Link
                  key={uc.path}
                  to={uc.path}
                  className="group flex items-start gap-3 bg-card border border-border rounded-xl p-5 hover:border-accent hover:shadow-lg transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <Compass className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground group-hover:text-accent transition-colors">
                      {uc.label}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{uc.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-16 max-w-3xl" id="buying-guide">
            <h2 className="font-heading font-bold text-2xl mb-6">How to Choose the Best Solar Panel</h2>
            <div className="space-y-5 text-muted-foreground">
              <p>
                <strong className="text-foreground">Start with your power budget, not the panel.</strong> List
                every device you want to run, its wattage, and daily hours of use. That gives you daily
                watt-hours — the number that determines how much solar you actually need. Use our{" "}
                <Link to="/solar-panel-calculator" className="text-accent-text underline underline-offset-2">
                  solar panel calculator
                </Link>{" "}
                to turn that list into a wattage recommendation.
              </p>
              <p>
                <strong className="text-foreground">Match the panel type to your mounting surface.</strong> Flat
                RV and van roofs work best with rigid framed panels. Curved roofs (Sprinter curves, Airstreams,
                boat decks) need flexible panels. Camping and mobile setups call for foldable portable panels.
                Choosing the wrong form factor is the most common solar buying mistake.
              </p>
              <p>
                <strong className="text-foreground">Efficiency matters when space is limited.</strong> On a
                ground-mounted array with unlimited space, efficiency is less important — buy the cheapest
                watt. On an RV roof, van roof, or portable panel where every square inch counts, high-efficiency
                panels (20%+) deliver meaningfully more power from the same footprint.
              </p>
              <p>
                <strong className="text-foreground">Check connector and voltage compatibility.</strong> If you're
                charging a power station (EcoFlow, Jackery, Bluetti), verify the panel's open-circuit voltage is
                within the station's solar input range and that you have the right adapter (MC4 to XT60, MC4 to
                8mm, etc.). Exceeding the voltage limit can damage your station.
              </p>
              <p>
                <strong className="text-foreground">Prioritize build quality over headline specs.</strong> A
                well-built 21% panel from a reputable brand will outperform and outlast a cheap 23% panel that
                delaminates in two years. Read verified reviews, check warranty terms (25-year output warranties
                are standard for quality rigid panels), and buy from brands with established track records.
              </p>
            </div>
          </div>

          <div className="mt-12 max-w-3xl" id="faq">
            <h2 className="font-heading font-bold text-2xl mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "What are the best solar panels overall?", a: "There is no single best panel — it depends on your use case. For permanent installations, high-efficiency monocrystalline rigid panels (Renogy, SunPower) are the best choice. For RVs and vans, rigid rooftop panels paired with an MPPT controller. For camping and portability, foldable panels like the BougeRV 200W or EcoFlow 400W. For EcoFlow power stations, match the panel voltage and connector to your specific EcoFlow model." },
                { q: "Who makes the best solar panels?", a: "For consumer and off-grid solar, Renogy, EcoFlow, BougeRV, and SunPower are among the most reputable brands. SunPower makes the most efficient commercially available panels (IBC cells, 22.8%+). Renogy offers the best value for RV and off-grid kits. EcoFlow and BougeRV lead in portable and power-station-compatible panels." },
                { q: "Which solar panel is best for an RV?", a: "Rigid monocrystalline panels (100-200W each) mounted on the roof are best for most RVs. For full-time boondocking, 400-800W is typical. Flexible panels are worth considering for curved roofs. See our dedicated RV solar panel guide for specific recommendations." },
                { q: "Are higher wattage solar panels always better?", a: "No. A 400W panel is only better than a 200W panel if you have the space to mount it and the power needs to justify it. Wattage tells you how much power a panel produces under ideal conditions — it says nothing about efficiency, build quality, or whether the panel fits your application." },
                { q: "What is the most efficient solar panel commercially available?", a: "The most efficient commercially available solar panels use IBC (interdigitated back contact) or HJT (heterojunction) cell technology and reach 22-24% efficiency. SunPower Maxeon panels are among the most efficient consumer panels available. See our high-efficiency solar panels guide for the full breakdown." },
              ].map((faq, i) => (
                <div key={i} className="border border-border rounded-xl p-5">
                  <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/solar-panel-calculator"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
            >
              Calculate Your Solar Needs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/solar-guides"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
            >
              Browse All Solar Guides
            </Link>
          </div>
        </div>
        <EmailCapture source="best-solar-panels" />
      </div>
    </>
  );
}